'use client';

import type { FC, PointerEvent as ReactPointerEvent, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from '../icons';
import { getPortalRoot } from '../portalRoot';
import { useUiMotionEnabled } from '../../hooks/useMotion';
import { useIsDesktop } from '../../hooks/useIsDesktop';

const DRAG_SLOP_PX = 6;
const CLOSE_RATIO = 0.25;
const FLING_VELOCITY = 0.5;
const FLING_MIN_OFFSET_PX = 40;
const SETTLE_MS = 260;

const openSheets: symbol[] = [];

interface DragState {
  pointerId: number;
  target: Element | null;
  startX: number;
  startY: number;
  lastY: number;
  lastT: number;
  velocity: number;
  decided: boolean;
  dragging: boolean;
}

const dragBelongsToSheet = (target: Element | null, panel: Element | null): boolean => {
  let node: Element | null = target;
  while (node && node !== panel) {
    if (node instanceof HTMLElement) {
      const overflowY = window.getComputedStyle(node).overflowY;
      const scrollable =
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        node.scrollHeight > node.clientHeight + 1;
      if (scrollable && node.scrollTop > 0) return false;
    }
    node = node.parentElement;
  }
  return true;
};

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  onDone?: () => void;
  doneAriaLabel?: string;
  closeAriaLabel?: string;
  headerAccessory?: ReactNode;
  scrollRef?: RefObject<HTMLDivElement | null>;
  bodyClassName?: string;
  panelClassName?: string;
  hideHeader?: boolean;
  heightClass?: string;
  children: ReactNode;
}

export const BottomSheet: FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onDone,
  doneAriaLabel,
  closeAriaLabel,
  headerAccessory,
  scrollRef,
  bodyClassName,
  panelClassName,
  hideHeader = false,
  heightClass = 'h-[90vh]',
  children,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const motionEnabled = useUiMotionEnabled();
  const isDesktop = useIsDesktop();

  const [dragOffset, setDragOffset] = useState<number | null>(null);
  const [isSettling, setIsSettling] = useState(false);
  const [wasDragged, setWasDragged] = useState(false);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      setIsExiting(false);
      return;
    }
    if (!isMounted) return;
    setIsExiting(true);
    const timer = window.setTimeout(() => setIsMounted(false), 340);
    return () => window.clearTimeout(timer);
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isMounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMounted]);

  useEffect(() => {
    if (!isMounted || isExiting) return;
    const token = Symbol('bottom-sheet');
    openSheets.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (openSheets[openSheets.length - 1] !== token) return;
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = openSheets.lastIndexOf(token);
      if (at !== -1) openSheets.splice(at, 1);
    };
  }, [isMounted, isExiting]);

  useEffect(() => {
    if (!isOpen) return;
    dragRef.current = null;
    setDragOffset(null);
    setIsSettling(false);
    setWasDragged(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isExiting || dragOffset !== 0) return;
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    setDragOffset(null);
    setIsSettling(false);
  }, [isExiting, dragOffset]);

  useEffect(() => () => {
    if (settleTimerRef.current !== null) window.clearTimeout(settleTimerRef.current);
  }, []);

  // WHY: React вешает touchmove пассивно — preventDefault в обработчике не работает, нужен свой non-passive listener
  useEffect(() => {
    const panel = panelRef.current;
    if (!isMounted || !panel || isDesktop) return;
    const onTouchMove = (e: TouchEvent) => {
      if (dragRef.current?.dragging) e.preventDefault();
    };
    panel.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => panel.removeEventListener('touchmove', onTouchMove);
  }, [isMounted, isDesktop]);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (dragRef.current) return;
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    dragRef.current = {
      pointerId: e.pointerId,
      target: e.target instanceof Element ? e.target : null,
      startX: e.clientX,
      startY: e.clientY,
      lastY: e.clientY,
      lastT: e.timeStamp,
      velocity: 0,
      decided: false,
      dragging: false,
    };
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    if (!drag.decided) {
      if (Math.abs(dx) < DRAG_SLOP_PX && Math.abs(dy) < DRAG_SLOP_PX) return;
      drag.decided = true;
      drag.dragging =
        dy > 0 &&
        Math.abs(dy) > Math.abs(dx) &&
        dragBelongsToSheet(drag.target, panelRef.current);
      if (!drag.dragging) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      window.getSelection()?.removeAllRanges();
      setIsSettling(false);
      setWasDragged(true);
    }
    if (!drag.dragging) return;

    const dt = e.timeStamp - drag.lastT;
    if (dt > 0) drag.velocity = (e.clientY - drag.lastY) / dt;
    drag.lastY = e.clientY;
    drag.lastT = e.timeStamp;
    setDragOffset(Math.max(0, dy));
  };

  const settleBack = () => {
    if (!motionEnabled) {
      setDragOffset(null);
      setIsSettling(false);
      return;
    }
    setIsSettling(true);
    setDragOffset(0);
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      setDragOffset(null);
      setIsSettling(false);
    }, SETTLE_MS);
  };

  const handlePointerEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (!drag.dragging) return;

    const offset = Math.max(0, e.clientY - drag.startY);
    const panelHeight = panelRef.current?.getBoundingClientRect().height ?? 0;
    const farEnough = panelHeight > 0 && offset > panelHeight * CLOSE_RATIO;
    const fastEnough = drag.velocity > FLING_VELOCITY && offset > FLING_MIN_OFFSET_PX;

    if (farEnough || fastEnough) {
      setIsSettling(motionEnabled);
      setDragOffset(panelHeight || window.innerHeight);
      onCloseRef.current();
      settleTimerRef.current = window.setTimeout(() => {
        settleTimerRef.current = null;
        if (!isOpenRef.current) return;
        setDragOffset(null);
        setIsSettling(false);
      }, SETTLE_MS + 60);
      return;
    }
    settleBack();
  };

  const handlePointerCancel = (e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragRef.current = null;
    if (drag.dragging) settleBack();
  };

  if (!isMounted) return null;

  const isDragDriven = dragOffset !== null;
  const panelStyle = isDragDriven && !isDesktop
    ? {
        transform: `translateY(${dragOffset}px)`,
        transition: isSettling ? `transform ${SETTLE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)` : 'none',
        willChange: 'transform',
        userSelect: 'none' as const,
      }
    : undefined;
  const panelAnimClass = isDesktop
    ? isExiting
      ? 'animate-shot-modal-out'
      : 'animate-shot-modal-in'
    : isDragDriven
      ? ''
      : isExiting
        ? 'animate-shot-sheet-slide-down'
        : wasDragged
          ? ''
          : 'animate-shot-sheet-slide-up';

  const desktopHeightClass = scrollRef ? 'h-[min(85vh,860px)]' : 'h-auto max-h-[85vh]';
  const panelFrameClass = isDesktop
    ? `relative w-full max-w-[520px] rounded-[28px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.6)] ${desktopHeightClass}`
    : `absolute start-0 end-0 bottom-0 max-w-[480px] mx-auto rounded-t-[36px] shadow-[0_-12px_40px_rgba(0,0,0,0.55)] ${heightClass}`;

  return createPortal(
    <div data-blocks-page-scroll className={`fixed inset-0 z-[1100] ${isDesktop ? 'flex items-center justify-center p-6' : ''}`}>
      <div
        className={`absolute inset-0 bg-black/45 backdrop-blur-md ${isExiting ? 'animate-shot-sheet-fade-out' : 'animate-shot-sheet-fade-in'}`}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        data-testid="bottom-sheet-panel"
        data-variant={isDesktop ? 'modal' : 'sheet'}
        style={panelStyle}
        onPointerDown={isDesktop ? undefined : handlePointerDown}
        onPointerMove={isDesktop ? undefined : handlePointerMove}
        onPointerUp={isDesktop ? undefined : handlePointerEnd}
        onPointerCancel={isDesktop ? undefined : handlePointerCancel}
        className={`${panelFrameClass} bg-bg-secondary flex flex-col ${panelClassName ?? ''} ${panelAnimClass}`}
      >
        {hideHeader ? (
          <>

            {!isDesktop && (
              <span
                data-testid="bottom-sheet-handle"
                aria-hidden
                className="absolute left-1/2 top-0 z-20 flex h-6 w-16 -translate-x-1/2 items-center justify-center touch-none"
              >
                <span className="block h-[5px] w-9 rounded-full bg-white/30" />
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className="absolute end-3 top-3 z-20 flex h-11 w-11 items-center justify-center text-label-secondary active:opacity-70 transition-opacity"
              aria-label={closeAriaLabel}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-fill-tertiary">
                <FiX size={16} />
              </span>
            </button>
          </>
        ) : (
          <>
            {!isDesktop && (
              <div
                data-testid="bottom-sheet-handle"
                aria-hidden
                className="shrink-0 flex justify-center pt-2 pb-1 touch-none"
              >
                <span className="block h-[5px] w-9 rounded-full bg-fill-tertiary" />
              </div>
            )}
            <div className={`grid grid-cols-[44px_1fr_44px] items-center px-4 pb-3 shrink-0 ${isDesktop ? 'pt-5' : 'pt-2'}`}>
            <button
              type="button"
              onClick={onClose}
              className="w-11 h-11 -my-1 flex items-center justify-start text-label-secondary active:opacity-70 transition-opacity justify-self-start"
              aria-label={closeAriaLabel}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-fill-tertiary">
                <FiX size={16} />
              </span>
            </button>
            <div className="flex flex-col items-center min-w-0">
              <div className="text-label-primary text-[15px] font-semibold text-center truncate tabular-nums">
                {title}
              </div>
              {subtitle != null && (
                <div className="text-label-primary text-[12px] font-semibold mt-0.5 tabular-nums">
                  {subtitle}
                </div>
              )}
            </div>
            {onDone ? (
              <button
                type="button"
                onClick={onDone}
                className="w-11 h-11 -my-1 text-label-primary flex items-center justify-end active:opacity-85 transition-opacity justify-self-end"
                aria-label={doneAriaLabel}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-blue">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            ) : (
              <span aria-hidden className="justify-self-end" />
            )}
            </div>
          </>
        )}
        {headerAccessory != null && (
          <div className="shrink-0 px-4 pb-2">{headerAccessory}</div>
        )}
        <div
          ref={scrollRef}
          data-testid="bottom-sheet-body"
          className={`flex-1 overflow-y-auto overscroll-contain px-4 pb-[max(20px,var(--tg-viewport-safe-area-inset-bottom,0px))] ${bodyClassName ?? ''}`}
        >
          {children}
        </div>
      </div>
    </div>,
    getPortalRoot(),
  );
};
